/**
 * Dashboard control plane (feature v2-live-engine). Reads run status from the simulator's
 * own control API (GET /control/state) and POSTs operator commands. The /metrics read path
 * stays untouched; this is the WRITE side (FR-TS-061), the simulator's own governed surface.
 *
 * Every /control/* route requires the operator bearer token (requireControlAuth in
 * src/api/control-routes.ts) — the public dashboard stays read-only (FR-TS-097; ADR-TS-0015)
 * until an operator unlocks it here with the token. The token lives only in this browser
 * tab's sessionStorage and is never bundled or hardcoded.
 */

const TOKEN_KEY = 'ts_operator_token';

export function getToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null; // sessionStorage unavailable (e.g. private mode) — treat as locked
  }
}

export function setToken(token: string): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore — token just won't persist */
  }
}

export function clearToken(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Reflect locked/unlocked state into the operator-lock widget and dim the control body.
 * `pointer-events: none` (styles.css) stops mouse interaction, but not keyboard Tab+Enter
 * activation of focusable elements underneath — so this also flips `disabled`/`tabIndex`
 * directly, making the locked body genuinely inert rather than only visually so.
 */
export function renderLockState(doc: Document, locked: boolean, error = ''): void {
  const panel = doc.querySelector<HTMLElement>('[data-control]');
  panel?.setAttribute('data-locked', locked ? 'true' : 'false');

  const lockEl = doc.querySelector<HTMLElement>('[data-operator-lock]');
  const unlockedEl = doc.querySelector<HTMLElement>('[data-operator-unlocked]');
  if (lockEl) lockEl.style.display = locked ? 'flex' : 'none';
  if (unlockedEl) unlockedEl.style.display = locked ? 'none' : 'flex';

  const errEl = doc.querySelector<HTMLElement>('[data-lock-error]');
  if (errEl) errEl.textContent = error;

  doc.querySelectorAll<HTMLElement>('[data-ctrl-body] button, [data-ctrl-body] input, [data-ctrl-body] select')
    .forEach((el) => {
      if (locked) {
        // Only capture the ORIGINAL tabindex on the first lock of a streak. Without this
        // guard, a second locked render before an intervening unlock (e.g. initControls()
        // starts locked, then a story/controlFetch call re-locks) would overwrite the saved
        // value with the current "-1" — permanently stranding the control at tabindex="-1"
        // even after a valid unlock, since the restore path had nothing real left to restore.
        if (!el.hasAttribute('data-was-tabindex')) {
          el.setAttribute('data-was-tabindex', el.getAttribute('tabindex') ?? '');
        }
        el.tabIndex = -1;
      } else {
        const prev = el.getAttribute('data-was-tabindex');
        if (prev) el.setAttribute('tabindex', prev); else el.removeAttribute('tabindex');
        el.removeAttribute('data-was-tabindex');
      }
      if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
        el.disabled = locked;
      }
    });
}

/**
 * Shared entry point for every /control/* call. Attaches the bearer token, and on 401/503
 * re-locks the panel with an explanatory message instead of leaving the caller to guess why
 * a control action silently did nothing. Exported so stories.ts (which also drives
 * /control/*) shares this instead of reimplementing its own unauthenticated fetch.
 */
export async function controlFetch(doc: Document, path: string, init: RequestInit = {}): Promise<Response | null> {
  const token = getToken();
  if (!token) {
    renderLockState(doc, true, 'Locked — enter the operator token to control the run.');
    return null;
  }
  try {
    const headers: Record<string, string> = {
      ...(init.headers as Record<string, string> | undefined),
      authorization: `Bearer ${token}`,
    };
    const r = await fetch(path, { ...init, headers });
    if (r.status === 401) {
      clearToken();
      renderLockState(doc, true, 'Invalid or expired token — enter it again.');
      return null;
    }
    if (r.status === 503) {
      renderLockState(doc, true, 'Control API is disabled on the server (TS_CONTROL_TOKEN not configured).');
      return null;
    }
    return r;
  } catch {
    return null;
  }
}

export type RunPhase = 'idle' | 'running' | 'paused' | 'halted';
export interface RunStatus {
  phase: RunPhase;
  source: 'simulated' | 'live';
  model: 'closed' | 'open';
  targetConcurrency: number;
  maxConcurrency: number;
  targetRatePerBank: number;
  delaySeconds: number;
  elapsedSeconds: number;
  totalConcurrency: number;
  aggregateTps: number;
  invariantBreaches: number;
  activeFaults: string[];
  enabledScenarios: string[];
  enabledBanks: string[];
  enabledFlows: string[];
  activeUsers: number;
}

const PHASE_LABEL: Record<RunPhase, string> = {
  idle: 'IDLE',
  running: 'RUNNING',
  paused: 'PAUSED',
  halted: 'HALTED — auto-stopped by breaker',
};

async function post(doc: Document, path: string, body?: unknown): Promise<RunStatus | null> {
  const init: RequestInit = { method: 'POST' };
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const r = await controlFetch(doc, path, init);
  if (!r || !r.ok) return null;
  return (await r.json()) as RunStatus;
}

async function getStatus(doc: Document): Promise<RunStatus | null> {
  const r = await controlFetch(doc, '/control/state', { cache: 'no-store' });
  if (!r || !r.ok) return null;
  return (await r.json()) as RunStatus;
}

/** Reflect run status into the control panel without fighting in-progress user input. */
export function renderRunStatus(doc: Document, s: RunStatus): void {
  const panel = doc.querySelector<HTMLElement>('[data-control]');
  if (panel) {
    panel.setAttribute('data-phase', s.phase);
    panel.setAttribute('data-source', s.source);
  }

  const set = (field: string, txt: string): void => {
    const el = doc.querySelector(`[data-field="${field}"]`);
    if (el) el.textContent = txt;
  };
  const shownTarget = Math.min(s.targetConcurrency, s.maxConcurrency);
  set('phase', PHASE_LABEL[s.phase]);
  set('source', s.source === 'live' ? 'LIVE — real bank calls' : 'simulated');
  set('elapsed', `${s.elapsedSeconds}s`);
  set('target', String(shownTarget));
  set('delay-label', `${s.delaySeconds}s`);
  set('users', String(s.activeUsers));

  // Make the concurrency slider reflect this source's real ceiling (e.g. 10 live, 200 simulated).
  const conc = doc.getElementById('concurrency') as HTMLInputElement | null;
  if (conc) conc.max = String(s.maxConcurrency);

  syncSlider(doc, 'concurrency', shownTarget);
  syncSlider(doc, 'delay', s.delaySeconds);

  // Button availability by phase.
  toggle(doc, 'start', s.phase === 'running');
  toggle(doc, 'stop', s.phase === 'idle');
  toggle(doc, 'pause', s.phase !== 'running');
  toggle(doc, 'resume', s.phase !== 'paused');

  doc.querySelectorAll<HTMLElement>('[data-fault]').forEach((btn) => {
    const kind = btn.getAttribute('data-fault') ?? '';
    btn.setAttribute('data-on', s.activeFaults.includes(kind) ? 'true' : 'false');
  });

  // Reflect which transaction types are enabled (lane toggles).
  doc.querySelectorAll<HTMLElement>('[data-scenario]').forEach((btn) => {
    const sc = btn.getAttribute('data-scenario') ?? '';
    btn.setAttribute('data-enabled', s.enabledScenarios.includes(sc) ? 'true' : 'false');
  });

  // Reflect which banks are enabled (bank-node toggles, FR-TS-124).
  doc.querySelectorAll<HTMLElement>('[data-bank]').forEach((node) => {
    const bank = node.getAttribute('data-bank') ?? '';
    const on = s.enabledBanks.includes(bank);
    node.setAttribute('data-enabled', on ? 'true' : 'false');
    const lbl = node.querySelector('[data-field="enabled-label"]');
    if (lbl) lbl.textContent = on ? 'ON' : 'OFF';
  });

  // Reflect which flow kinds are enabled (Flows-panel toggles, FR-TS-129).
  doc.querySelectorAll<HTMLElement>('button[data-flow]').forEach((btn) => {
    const kind = btn.getAttribute('data-flow') ?? '';
    btn.setAttribute('data-enabled', (s.enabledFlows ?? []).includes(kind) ? 'true' : 'false');
  });
}

function syncSlider(doc: Document, id: string, value: number): void {
  const el = doc.getElementById(id) as HTMLInputElement | null;
  if (el && doc.activeElement !== el) el.value = String(value);
}

function toggle(doc: Document, cmd: string, disabled: boolean): void {
  const el = doc.querySelector<HTMLButtonElement>(`[data-cmd="${cmd}"]`);
  if (el) el.disabled = disabled;
}

/** Wire buttons + sliders + fault controls, and keep status fresh. */
export function initControls(doc: Document = document, pollMs = 1000): void {
  // Any successful /control/* response proves the token is valid, so unlock on every one —
  // controlFetch() already re-locks (with an explanatory message) on 401/503.
  const apply = (s: RunStatus | null): void => {
    if (s) {
      renderLockState(doc, false);
      renderRunStatus(doc, s);
    }
  };

  renderLockState(doc, getToken() === null);

  const lockForm = doc.querySelector<HTMLFormElement>('[data-lock-form]');
  const lockInput = doc.querySelector<HTMLInputElement>('[data-lock-token]');
  const lockAgainBtn = doc.querySelector<HTMLButtonElement>('[data-lock-again]');

  lockForm?.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const value = lockInput?.value.trim() ?? '';
    if (!value) return;
    setToken(value);
    if (lockInput) lockInput.value = '';
    // Prove the token works with a read before treating the panel as unlocked.
    void getStatus(doc).then(apply);
  });

  lockAgainBtn?.addEventListener('click', () => {
    clearToken();
    renderLockState(doc, true);
  });

  doc.querySelectorAll<HTMLButtonElement>('[data-cmd]').forEach((btn) => {
    btn.addEventListener('click', () => {
      void post(doc, `/control/${btn.getAttribute('data-cmd')}`).then(apply);
    });
  });

  const concurrency = doc.getElementById('concurrency') as HTMLInputElement | null;
  concurrency?.addEventListener('input', () => {
    const label = doc.querySelector('[data-field="target"]');
    if (label) label.textContent = concurrency.value;
    void post(doc, '/control/config', { concurrency: Number(concurrency.value) }).then(apply);
  });

  const delay = doc.getElementById('delay') as HTMLInputElement | null;
  delay?.addEventListener('input', () => {
    const label = doc.querySelector('[data-field="delay-label"]');
    if (label) label.textContent = `${Number(delay.value)}s`;
    void post(doc, '/control/config', { delaySeconds: Number(delay.value) }).then(apply);
  });

  // Click a lane to enable/disable that transaction type.
  doc.querySelectorAll<HTMLElement>('[data-scenario]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const scenario = btn.getAttribute('data-scenario') ?? '';
      const on = btn.getAttribute('data-enabled') !== 'true';
      void post(doc, '/control/scenario', { scenario, on }).then(apply);
    });
  });

  // Click a bank node to enable/disable that whole bank (FR-TS-124).
  doc.querySelectorAll<HTMLElement>('[data-bank]').forEach((node) => {
    node.addEventListener('click', () => {
      const bank = node.getAttribute('data-bank') ?? '';
      const on = node.getAttribute('data-enabled') !== 'true';
      void post(doc, '/control/bank', { bank, on }).then(apply);
    });
  });

  // Click a flow toggle to enable/disable that flow kind (FR-TS-129).
  doc.querySelectorAll<HTMLElement>('button[data-flow]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const kind = btn.getAttribute('data-flow') ?? '';
      const on = btn.getAttribute('data-enabled') !== 'true';
      void post(doc, '/control/flow', { kind, on }).then(apply);
    });
  });

  doc.querySelectorAll<HTMLElement>('[data-fault]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const kind = btn.getAttribute('data-fault') ?? '';
      const on = btn.getAttribute('data-on') !== 'true';
      void post(doc, '/control/fault', { kind, on }).then(apply);
    });
  });

  const clearBtn = doc.querySelector('[data-fault-clear]');
  clearBtn?.addEventListener('click', () => void post(doc, '/control/fault/clear').then(apply));

  // Fire a compliance trigger (sanctions hit / a specific fraud rule) on demand.
  doc.querySelectorAll<HTMLElement>('[data-trigger]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const kind = btn.getAttribute('data-trigger') ?? '';
      btn.setAttribute('data-fired', 'true');
      window.setTimeout(() => btn.removeAttribute('data-fired'), 600);
      void post(doc, '/control/trigger', { kind }).then(apply);
    });
  });

  // ---- "Try it" panel — fire a custom payment on demand (v9-interactive) ----
  let selectedDebtor = 'alice';

  doc.querySelectorAll<HTMLElement>('[data-try-persona]').forEach((btn) => {
    if (btn.getAttribute('data-try-persona') === selectedDebtor) btn.setAttribute('data-selected', 'true');
    btn.addEventListener('click', () => {
      selectedDebtor = btn.getAttribute('data-try-persona') ?? 'alice';
      doc.querySelectorAll('[data-try-persona]').forEach((b) => b.removeAttribute('data-selected'));
      btn.setAttribute('data-selected', 'true');
    });
  });

  const scenarioSel = doc.querySelector<HTMLSelectElement>('[data-try-scenario]');
  const amountInput = doc.querySelector<HTMLInputElement>('[data-try-amount]');
  const fireBtn = doc.querySelector<HTMLButtonElement>('[data-try-fire]');
  const resultEl = doc.querySelector<HTMLElement>('[data-try-result]');

  fireBtn?.addEventListener('click', () => {
    const scenario = scenarioSel?.value ?? 'sct';
    const eur = parseFloat(amountInput?.value ?? '100');
    if (isNaN(eur) || eur <= 0) return;
    const amountCents = Math.round(eur * 100);
    if (resultEl) { resultEl.style.display = 'block'; resultEl.textContent = 'Firing…'; }
    void post(doc, '/control/fire', { debtor: selectedDebtor, scenario, amountCents }).then((s) => {
      apply(s);
      if (resultEl) {
        resultEl.textContent = s ? `✓ Fired ${selectedDebtor} → ${scenario}` : '✗ Failed (simulator not running?)';
        window.setTimeout(() => { if (resultEl) resultEl.style.display = 'none'; }, 2500);
      }
    });
  });

  // Only poll while a token is present — polling with none would fire an authless
  // controlFetch() every tick, and its "Locked — enter the operator token..." message would
  // overwrite a more specific one (e.g. "Invalid or expired token") within `pollMs`.
  const pollIfUnlocked = (): void => { if (getToken()) void getStatus(doc).then(apply); };
  pollIfUnlocked();
  window.setInterval(pollIfUnlocked, pollMs);
}
